import deepmergeLib from 'deepmerge';

type ArrayMergePreset = 'source' | 'target' | 'combine';
type ArrayMergeHandler = NonMaybe<deepmergeLib.Options['arrayMerge']>;
export type DeepMergeOptions = Readonly<{
  /**
   * @default 'source'
   */
  arrayMerge?: ArrayMergePreset | ArrayMergeHandler;
}>;

// https://github.com/TehShrike/deepmerge?tab=readme-ov-file#arraymerge-example-combine-arrays
const combineMerge: ArrayMergeHandler = (target, source: unknown[], options?: deepmergeLib.Options) => {
  const destination = [...target];

  source.forEach((item, index) => {
    if (typeof destination[index] === 'undefined') {
      destination[index] = item;
    } else if (item && typeof item === 'object') {
      destination[index] = deepmergeLib(target[index], item, options || {});
    } else if (target.indexOf(item) === -1) {
      destination.push(item);
    }
  });

  return destination;
};

const arrayMergePresetFns: ReadonlyRecord<ArrayMergePreset, ArrayMergeHandler> = {
  source: (target, source) => source,
  target: (target, source) => target,
  combine: combineMerge,
};

const _defaultArrayMerge = (preset: ArrayMergePreset, target: unknown[], source: unknown[], opts?: deepmergeLib.Options) => {
  const handler = arrayMergePresetFns[preset] || arrayMergePresetFns.source;
  return handler(target, source, opts);
};

const _shouldUsePreset = (arrayMerge: NonMaybe<DeepMergeOptions['arrayMerge']>): arrayMerge is ArrayMergePreset => {
  return ['source', 'target', 'combine'].includes(arrayMerge as string);
};

export const deepmerge = <T1, T2>(x: T1, y: T2, opts: DeepMergeOptions = {}) => {
  const { arrayMerge = 'source' } = opts;
  return deepmergeLib<T1, T2>(x, y, {
    arrayMerge: (...args) => (_shouldUsePreset(arrayMerge) ? _defaultArrayMerge(arrayMerge, ...args) : arrayMerge(...args)),
  });
};
